import { Link } from "react-router-dom";
import { useSite } from "@/hooks/useSite";
import { LoadState } from "@/components/ErrorBoundary";
import { Seo } from "@/components/Seo";
import { Footer } from "@/components/site/Footer";
import { ArrowLeft, MapPin, Route, Trophy, QrCode, BookOpen, Users } from "lucide-react";

const FEATURES = [
  { icon: MapPin, title: "Reale Stationen", body: "Orte im Ort werden zu Stationen – erreichbar über QR-Codes und kurze Wege." },
  { icon: Route, title: "Routen & Missionen", body: "Zusammenhängende Routen mit Aufgaben, die zum Weitergehen einladen." },
  { icon: BookOpen, title: "Lokale Geschichten", body: "Foto-, Text- und Audioinhalte erzählen, was an einem Ort passiert ist." },
  { icon: Trophy, title: "Challenges & Belohnungen", body: "Sportliche Aufgaben, Sammelobjekte und kleine Belohnungen." },
  { icon: Users, title: "Rankings & Duelle", body: "Rankings, Duelle und eine Pinnwand als mögliche Erweiterungen." },
  { icon: QrCode, title: "Übertragbar", body: "Später auf andere Gemeinden, Regionen und Kunstwege übertragbar." },
];

export default function Entdecken() {
  const { site, status, error, reload } = useSite();
  if (!site) return <LoadState status={status} error={error} onRetry={reload} label="Wird geladen" />;

  return (
    <>
      <Seo
        title="Entdecke Orte neu – Digitales Entdeckerprojekt von René Ghavami"
        description="Eine digitale Plattform, die reale Orte mit Geschichten, Aufgaben, Challenges und Sammelobjekten verbindet. Pilotprojekt in St. Ruprecht an der Raab."
        path="/entdecken"
      />
      <main className="bg-paper">
        <header className="border-b border-paper-line bg-paper">
          <div className="ap-container flex h-[72px] items-center justify-between">
            <Link to="/" className="font-display text-xl font-black uppercase tracking-[0.14em] text-ink" data-testid="entdecken-logo">
              Art<span className="text-signal">Part</span>
            </Link>
            <Link to="/" className="ap-stamp transition-colors hover:text-signal" data-testid="entdecken-back">
              Zurück zu ArtPart
            </Link>
          </div>
        </header>

        <section className="border-b border-paper-line bg-paper-surface py-16 sm:py-24" data-testid="entdecken-hero">
          <div className="ap-container max-w-3xl">
            <p className="ap-stamp">PILOTPROJEKT · ST. RUPRECHT AN DER RAAB</p>
            <h1 className="ap-h1 mt-5 text-ink">Entdecke Orte neu.</h1>
            <p className="ap-lead mt-6">
              Eine digitale Plattform, die reale Orte mit Geschichten, Aufgaben, Challenges und Sammelobjekten verbindet.
            </p>
            <div className="mt-9 flex flex-col gap-3 sm:flex-row">
              <a href="#pilot" className="ap-btn-solid" data-testid="entdecken-cta-pilot">Pilotprojekt ansehen</a>
              <a href="/#anfrage" className="ap-btn-ghost" data-testid="entdecken-cta-gemeinde">
                Projekt für eine Gemeinde anfragen
              </a>
            </div>
          </div>
        </section>

        <section id="pilot" className="border-b border-paper-line py-16 sm:py-24">
          <div className="ap-container">
            <h2 className="ap-h2 text-ink">Was das Projekt kann</h2>
            <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {FEATURES.map(({ icon: Icon, title, body }, i) => (
                <article key={title} data-testid={`entdecken-feature-${i}`} className="border border-paper-line bg-paper-surface p-6">
                  <Icon size={20} className="text-signal" />
                  <h3 className="mt-4 font-display text-lg font-bold uppercase tracking-wide text-ink">{title}</h3>
                  <p className="ap-body mt-2 text-charcoal">{body}</p>
                </article>
              ))}
            </div>
            <p className="ap-body mt-10 border-l-2 border-signal pl-4 text-charcoal">
              Gestartet wird als kleiner Pilot mit wenigen Stationen und Testpersonen in St. Ruprecht an der Raab.
            </p>
          </div>
        </section>

        <section className="bg-ink py-16 sm:py-20">
          <div className="ap-container max-w-2xl">
            <p className="ap-stamp">AP · INITIATIVE</p>
            <p className="mt-5 text-lg leading-relaxed text-paper sm:text-xl">
              Entwickelt von René Ghavami – Tätowierer, Gestalter und Initiator des Projekts.
            </p>
            <Link to="/" className="ap-btn-invert mt-8" data-testid="entdecken-footer-back">
              <ArrowLeft size={15} /> ArtPart Tattoo
            </Link>
          </div>
        </section>
      </main>
      <Footer texts={site.texts} />
    </>
  );
}
